import {
  CommandLineIcon,
  EyeSlashIcon,
  ServerStackIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FieldLabel } from '../../components/ui/FieldLabel';
import {
  TechnologyDistribution,
  getTechnologyDistribution,
} from '../../services/technologyApi';
import { getRequestErrorMessage } from '../../utils/getRequestErrorMessage';
import { TechnologyDistributionApplicationCard } from './TechnologyDistributionApplicationCard';
import { TechnologyDistributionDetailPanel } from './TechnologyDistributionDetailPanel';
import { TechnologyDistributionServerCard } from './TechnologyDistributionServerCard';
import { TECHNOLOGY_ENVIRONMENT_KIND_OPTIONS } from './technologyFormOptions';
import {
  DistributionFilters,
  countApplications,
  countUnmonitored,
  filterDistribution,
  groupByApplication,
} from './technologyDistributionView';

type DistributionMode = 'server' | 'application';

const EMPTY_FILTERS: DistributionFilters = {
  productId: '',
  environmentKind: '',
  assetId: '',
  search: '',
};

function normalizeDistribution(data: TechnologyDistribution): TechnologyDistribution {
  return {
    products: data.products ?? [],
    assets: (data.assets ?? []).map((asset) => ({
      ...asset,
      hostings: (asset.hostings ?? []).map((hosting) => ({
        ...hosting,
        products: hosting.products ?? [],
      })),
    })),
  };
}

export function TechnologyApplicationDistribution() {
  const [distribution, setDistribution] = useState<TechnologyDistribution | null>(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<DistributionFilters>(EMPTY_FILTERS);
  const [mode, setMode] = useState<DistributionMode>('server');
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [selectedApplicationId, setSelectedApplicationId] = useState<string | null>(null);
  const detailRef = useRef<HTMLDivElement>(null);
  const requestId = useRef(0);

  useEffect(() => {
    const current = requestId.current + 1;
    requestId.current = current;
    setLoading(true);
    void getTechnologyDistribution()
      .then((result) => {
        if (requestId.current !== current) return;
        setDistribution(normalizeDistribution(result));
      })
      .catch((error: unknown) => {
        if (requestId.current !== current) return;
        toast.error(getRequestErrorMessage(error, 'Falha ao carregar a distribuição de aplicações.'));
      })
      .finally(() => {
        if (requestId.current === current) setLoading(false);
      });
  }, []);

  const visible = useMemo(
    () => filterDistribution(distribution?.assets ?? [], filters),
    [distribution, filters],
  );
  const applications = useMemo(() => groupByApplication(visible), [visible]);
  const selectedServer = visible.find((item) => item.asset.id === selectedAssetId) ?? null;
  const selectedApplication =
    applications.find((item) => item.applicationId === selectedApplicationId) ?? null;

  useEffect(() => {
    if (selectedAssetId && !selectedServer) setSelectedAssetId(null);
  }, [selectedAssetId, selectedServer]);

  useEffect(() => {
    if (selectedApplicationId && !selectedApplication) setSelectedApplicationId(null);
  }, [selectedApplicationId, selectedApplication]);

  useEffect(() => {
    const selected = mode === 'server' ? selectedAssetId : selectedApplicationId;
    if (!selected) return;
    detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [mode, selectedApplicationId, selectedAssetId]);

  function openServer(assetId: string) {
    setMode('server');
    setSelectedAssetId(assetId);
  }

  const detailOpen = mode === 'server' ? selectedServer != null : selectedApplication != null;
  const serverCount = visible.length;
  const applicationCount = countApplications(visible);
  const unmonitoredCount = countUnmonitored(visible);

  return (
    <section>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="max-w-3xl">
          <h2 className="text-base font-semibold text-secondary-900">Distribuição de Aplicações</h2>
          <p className="mt-1 text-sm text-secondary-500">
            Cada infraestrutura mostra as aplicações hospedadas, com o ambiente no subtítulo.
            A capacidade (vCPU, memória e disco) vem do cadastro. Sem telemetria, a utilização fica
            como Não monitorado.
          </p>
        </div>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <SummaryCard
          label="Infraestruturas"
          value={loading ? null : serverCount}
          icon={ServerStackIcon}
          iconClassName="bg-sky-50 text-sky-600"
        />
        <SummaryCard
          label="Aplicações"
          value={loading ? null : applicationCount}
          icon={CommandLineIcon}
          iconClassName="bg-emerald-50 text-emerald-600"
        />
        <SummaryCard
          label="Não monitorados"
          value={loading ? null : unmonitoredCount}
          icon={EyeSlashIcon}
          iconClassName="bg-secondary-100 text-secondary-500"
          hint="Sem percentual de utilização. Não significa que o recurso está ocioso."
        />
      </div>

      <div className="mt-4 flex flex-col gap-3 xl:flex-row xl:items-end">
        <div className="grid flex-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <label className="erp-label">
            <FieldLabel
              description="Restringe às aplicações hospedadas neste ambiente."
              example="Produção"
            >
              Ambientes
            </FieldLabel>
            <select
              className="erp-input"
              value={filters.environmentKind}
              onChange={(event) =>
                setFilters((current) => ({ ...current, environmentKind: event.target.value }))
              }
            >
              <option value="">Todos os ambientes</option>
              {TECHNOLOGY_ENVIRONMENT_KIND_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="erp-label">
            <FieldLabel
              description="Mostra só as aplicações vinculadas a este produto."
              example="Plataforma Go"
            >
              Produtos
            </FieldLabel>
            <select
              className="erp-input"
              value={filters.productId}
              onChange={(event) =>
                setFilters((current) => ({ ...current, productId: event.target.value }))
              }
            >
              <option value="">Todos os produtos</option>
              {(distribution?.products ?? []).map((product) => (
                <option key={product.id} value={product.id}>
                  {product.name}
                </option>
              ))}
            </select>
          </label>
          <label className="erp-label">
            <FieldLabel
              description="Restringe a um ativo do inventário de infraestrutura."
              example="VPS-PROD-01"
            >
              Infraestrutura
            </FieldLabel>
            <select
              className="erp-input"
              value={filters.assetId}
              onChange={(event) =>
                setFilters((current) => ({ ...current, assetId: event.target.value }))
              }
            >
              <option value="">Todas as infraestruturas</option>
              {(distribution?.assets ?? []).map((asset) => (
                <option key={asset.id} value={asset.id}>
                  {asset.name}
                </option>
              ))}
            </select>
          </label>
          <label className="erp-label">
            Busca
            <input
              className="erp-input"
              value={filters.search}
              placeholder="Buscar aplicação ou servidor..."
              onChange={(event) =>
                setFilters((current) => ({ ...current, search: event.target.value }))
              }
            />
          </label>
        </div>
        <div className="erp-tab-bar shrink-0" role="tablist" aria-label="Forma de navegação">
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'server'}
            className={`erp-tab inline-flex items-center gap-1.5 ${mode === 'server' ? 'erp-tab--active' : 'erp-tab--inactive'}`}
            onClick={() => setMode('server')}
          >
            <Squares2X2Icon className="h-4 w-4" aria-hidden="true" />
            Visão por servidor
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'application'}
            className={`erp-tab inline-flex items-center gap-1.5 ${mode === 'application' ? 'erp-tab--active' : 'erp-tab--inactive'}`}
            onClick={() => setMode('application')}
          >
            <CommandLineIcon className="h-4 w-4" aria-hidden="true" />
            Visão por aplicação
          </button>
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-4 xl:flex-row xl:items-start">
        <div className={`min-w-0 flex-1 ${detailOpen ? 'order-2 xl:order-1' : ''}`}>
          {loading ? (
            <p className="text-sm text-secondary-500">Carregando...</p>
          ) : !distribution || distribution.assets.length === 0 ? (
            <p className="text-sm text-secondary-500">
              Nenhuma infraestrutura cadastrada. Inclua os servidores em{' '}
              <Link className="erp-link" to="/technology/infrastructure">
                Infraestrutura
              </Link>
              .
            </p>
          ) : visible.length === 0 ? (
            <p className="text-sm text-secondary-500">
              Nenhum resultado para os filtros selecionados.
            </p>
          ) : mode === 'application' && applications.length === 0 ? (
            <p className="text-sm text-secondary-500">
              Nenhuma aplicação hospedada corresponde aos filtros.
            </p>
          ) : mode === 'server' ? (
            <div className="grid gap-4 lg:grid-cols-2">
              {visible.map((item) => (
                <TechnologyDistributionServerCard
                  key={item.asset.id}
                  item={item}
                  selected={item.asset.id === selectedAssetId}
                  onSelect={() =>
                    setSelectedAssetId((current) => (current === item.asset.id ? null : item.asset.id))
                  }
                />
              ))}
            </div>
          ) : (
            <div className="grid gap-4 lg:grid-cols-2">
              {applications.map((group) => (
                <TechnologyDistributionApplicationCard
                  key={group.applicationId}
                  group={group}
                  selected={group.applicationId === selectedApplicationId}
                  onSelect={() =>
                    setSelectedApplicationId((current) =>
                      current === group.applicationId ? null : group.applicationId,
                    )
                  }
                  onOpenServer={openServer}
                />
              ))}
            </div>
          )}
        </div>
        {detailOpen ? (
          <div ref={detailRef} className="order-1 w-full shrink-0 xl:sticky xl:top-4 xl:order-2 xl:w-96">
            <TechnologyDistributionDetailPanel
              mode={mode}
              server={selectedServer}
              application={selectedApplication}
              onClose={() => {
                if (mode === 'server') setSelectedAssetId(null);
                else setSelectedApplicationId(null);
              }}
              onOpenServer={openServer}
            />
          </div>
        ) : null}
      </div>

      <footer className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-secondary-100 pt-3 text-xs text-secondary-500">
        <span className="font-medium text-secondary-600">Legenda</span>
        <LegendDot className="bg-emerald-500" label="Baixa utilização até 39%" />
        <LegendDot className="bg-amber-400" label="Uso moderado 40%–69%" />
        <LegendDot className="bg-orange-500" label="Alta utilização 70%–89%" />
        <LegendDot className="bg-rose-500" label="Crítico a partir de 90%" />
        <LegendDot className="bg-secondary-300" label="Não monitorado" />
        <span>
          As faixas valem quando houver medição. Sem telemetria, o status é Não monitorado e a
          capacidade exibida é a do cadastro.
        </span>
      </footer>
    </section>
  );
}

function SummaryCard({
  label,
  value,
  icon: Icon,
  iconClassName,
  hint,
}: {
  label: string;
  value: number | null;
  icon: typeof ServerStackIcon;
  iconClassName: string;
  hint?: string;
}) {
  const numberFormat = new Intl.NumberFormat('pt-BR');
  return (
    <article
      className="flex items-center gap-3 rounded-2xl border border-secondary-200 bg-white px-4 py-3 shadow-sm"
      title={hint}
    >
      <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${iconClassName}`}>
        <Icon className="h-5 w-5" aria-hidden="true" />
      </span>
      <span className="min-w-0">
        <span className="block text-2xl font-semibold leading-none text-secondary-900">
          {value == null ? '…' : numberFormat.format(value)}
        </span>
        <span className="mt-1 block truncate text-sm text-secondary-500">{label}</span>
      </span>
    </article>
  );
}

function LegendDot({ className, label }: { className: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`h-2.5 w-2.5 rounded-full ${className}`} aria-hidden="true" />
      {label}
    </span>
  );
}
